import type { CSSProperties, MouseEventHandler } from 'react';
import React from 'react';
import { onKeyDown as handleKeyDown } from '../../../helper/KeyDown';
import { mergeStyles } from '../../../helper/UtilityFunctions';
import styles from './Button.module.scss';

type ButtonProps = {
  children?: React.ReactNode;
  onClick?: MouseEventHandler;
  disabled?: boolean;
  secondary?: boolean;
  small?: boolean;
  inlineBlock?: boolean;
  style?: CSSProperties | CSSProperties[];
  className?: string;
  delete?: boolean;
};

export function Button(props: ButtonProps) {
  let effectiveStyle;
  if (Array.isArray(props.style)) {
    effectiveStyle = mergeStyles(props.style);
  } else {
    effectiveStyle = props.style;
  }

  let effectiveClasses = styles.Button;

  if (props.className) {
    effectiveClasses += ` ${props.className}`;
  }

  if (props.small) {
    effectiveClasses += ` ${styles.small}`;
  }

  if (props.inlineBlock) {
    effectiveClasses += ` ${styles.inlineBlock}`;
  }

  if (props.disabled) {
    effectiveClasses += ` ${styles.disabled}`;
  }

  if (props.secondary) {
    effectiveClasses += ` ${styles.secondary}`;
  }

  if (props.delete) {
    effectiveClasses += ` ${styles.delete}`;
  }

  const onKeyDown = handleKeyDown([
    {
      keys: ['Enter', ' '],
      handler: (event) => {
        if (props.onClick) {
          props.onClick(event as unknown as React.MouseEvent<HTMLDivElement>);
        }
      },
    },
    {
      keys: ['Escape'],
      handler: (event) => event.stopPropagation(),
      preventDefault: false,
    },
  ]);

  return (
    <div
      style={effectiveStyle}
      className={effectiveClasses}
      onClick={!props.disabled ? props.onClick : undefined}
      onKeyDown={onKeyDown}
      role='button'
      tabIndex={0}
    >
      {props.children}
    </div>
  );
}

type SubmitButtonProps = {
  children?: React.ReactNode;
  disabled?: boolean;
  className?: string;
};

export function SubmitButton(props: SubmitButtonProps) {
  let effectiveClasses = styles.Button;

  if (props.className) {
    effectiveClasses += ` ${props.className}`;
  }

  if (props.disabled) {
    effectiveClasses += ` ${styles.disabled}`;
  }

  return (
    <button type='submit' className={effectiveClasses} tabIndex={0} disabled={props.disabled}>
      {props.children}
    </button>
  );
}

type LinkButtonProps = {
  href: string;
  target?: string;
  style?: CSSProperties;
  children: React.ReactNode;
};

export function LinkButton({ href, target, style, children }: LinkButtonProps): JSX.Element {
  return (
    <a className={styles.Button} href={href} style={style} target={target}>
      {children}
    </a>
  );
}
